import type { FieldTree } from '@angular/forms/signals';
import { readonly } from '@angular/forms/signals';

import type { BorderSettings, BorderSide, Device, Side } from '@sites/entity-core';
import { SIDES, borderFeature, createBorderSide } from '@sites/entity-core';
import { ensureChild, writeDevice } from '../../core/write-device';

// Thunk is mandatory: the form model starts empty, so the border FieldTree node
// does not exist until the entity is synced.
export const createBorderAccessors = (getField: () => FieldTree<BorderSettings>) => {
    /** A contour that was off stores no sides; the first write seeds them. */
    const sideNode = (side: Side) => ensureChild(getField(), side, () => ({ desktop: createBorderSide() }));

    return {
        get border() {
            return getField();
        },
        // version is stamped by the form; callers never manage it
        setBorder: (value: Omit<BorderSettings, 'version'>) => {
            getField()().value.set({ ...value, version: borderFeature.latestVersion });
        },

        setBorderEnabled: (value: boolean, device: Device = 'desktop') => {
            writeDevice(getField().isEnabled, device, () => value);

            if (value) {
                SIDES.forEach(sideNode);
            }
        },
        setBorderSide: (side: Side, patch: Partial<BorderSide>, device: Device = 'desktop') => {
            writeDevice(sideNode(side), device, (current) => ({ ...current, ...patch }));
        },
        /** The independent-sides switch off, so all four move together. */
        setBorderAllSides: (patch: Partial<BorderSide>, device: Device = 'desktop') => {
            SIDES.forEach((side) => {
                writeDevice(sideNode(side), device, (current) => ({ ...current, ...patch }));
            });
        },
    };
};

export type BorderAccessors = ReturnType<typeof createBorderAccessors>;

// Schema-path rules: keep the nested feature version read-only, like
// schemaPath.version at entity level.
export const applyBorderFieldRules = (borderPath: any): void => {
    readonly(borderPath.version);
};
